import { computed } from 'vue';
import { ptBR } from 'date-fns/locale';
import { useLocale } from './useLocale';

/**
 * date-fns 2.x has no global locale (`setDefaultOptions` only exists from 3.x),
 * so every call that renders words — `formatDistanceToNow`, `format` with
 * textual tokens — has to receive one explicitly. Locales are mapped only where
 * the product copy around the date is also translated: a Portuguese timestamp
 * inside an English sentence reads worse than a fully English one.
 */
const DATE_FNS_LOCALES = {
  'pt-BR': ptBR,
};

/**
 * Composable for date-fns formatting with i18n locale support.
 * Reuses the same locale resolution as the Intl-based formatters
 * (`useLocale`), so both stay on the same normalized tag.
 *
 * @returns {{ dateFnsLocale: import('vue').ComputedRef<Object|undefined> }}
 *   The date-fns locale to pass as `{ locale }`, or `undefined` to keep the
 *   date-fns default (en-US).
 *
 * @example
 * const { dateFnsLocale } = useDateFnsLocale();
 * formatDistanceToNow(date, { locale: dateFnsLocale.value });
 */
export function useDateFnsLocale() {
  const { resolvedLocale } = useLocale();

  const dateFnsLocale = computed(() => DATE_FNS_LOCALES[resolvedLocale.value]);

  return { dateFnsLocale };
}
